import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AuthRoles_Enum,
  GetUsersDataQuery,
  GetUsersDataQueryVariables,
  Order_By,
  Organization_Status_Enum,
  Users_Bool_Exp,
  useGetUsersDataQuery,
} from "../../api/generated/graphql";
import { OrderBy } from "../../api/generated/secondary-graphql";

type UserRow = GetUsersDataQuery["users"][number];
type RoleFilter = "all" | string;
type OrgStatusFilter = "all" | "active" | "inactive" | "no_org";

const PAGE_SIZE = 20;

function normalize(value?: string | null) {
  return (value ?? "").trim().toLowerCase();
}

function formatDate(dateValue?: string | null) {
  if (!dateValue) return "-";
  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) return "-";
  const d = parsed.getDate().toString().padStart(2, "0");
  const m = (parsed.getMonth() + 1).toString().padStart(2, "0");
  const y = parsed.getFullYear();
  return `${d}/${m}/${y}`;
}

function formatDateTime(dateValue?: string | null) {
  if (!dateValue) return "-";
  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) return "-";
  const d = parsed.getDate().toString().padStart(2, "0");
  const m = (parsed.getMonth() + 1).toString().padStart(2, "0");
  const y = parsed.getFullYear();
  const h = parsed.getHours().toString().padStart(2, "0");
  const min = parsed.getMinutes().toString().padStart(2, "0");
  return `${d}/${m}/${y} ${h}:${min}`;
}

function getOrgStatusPillClasses(status?: string | null) {
  const normalized = normalize(status);
  if (normalized === "active") {
    return "bg-green-100 text-green-800";
  }
  if (normalized === "inactive") {
    return "bg-blue-100 text-blue-800";
  }
  if (!normalized) {
    return "bg-gray-100 text-gray-700";
  }
  return "bg-amber-100 text-amber-800";
}

function getSubscriptionPillClasses(status?: string | null) {
  const normalized = normalize(status);
  if (normalized === "active") {
    return "bg-emerald-100 text-emerald-800";
  }
  if (normalized === "expired" || normalized === "inactive") {
    return "bg-gray-100 text-gray-700";
  }
  if (!normalized) {
    return "bg-slate-100 text-slate-700";
  }
  return "bg-indigo-100 text-indigo-800";
}

function getUserRoles(user: UserRow) {
  return (user.roles ?? []).map((entry) => entry.role).filter(Boolean);
}

function getPageNumbers(totalPages: number, currentPage: number) {
  const pages: Array<number | "..."> = [];
  const maxVisiblePages = 7;

  if (totalPages <= maxVisiblePages) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
    return pages;
  }

  if (currentPage <= 4) {
    for (let i = 1; i <= 5; i++) pages.push(i);
    pages.push("...");
    pages.push(totalPages);
    return pages;
  }

  if (currentPage >= totalPages - 3) {
    pages.push(1);
    pages.push("...");
    for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
    return pages;
  }

  pages.push(1);
  pages.push("...");
  for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
  pages.push("...");
  pages.push(totalPages);
  return pages;
}

export default function Users() {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [roleFilter, setRoleFilter] = useState<RoleFilter>("all");
  const [orgStatusFilter, setOrgStatusFilter] =
    useState<OrgStatusFilter>("all");

  const whereClause = useMemo<Users_Bool_Exp>(() => {
    const conditions: Users_Bool_Exp[] = [];
    const trimmedSearch = debouncedSearchTerm.trim();

    if (trimmedSearch) {
      conditions.push({
        _or: [
          { displayName: { _ilike: `%${trimmedSearch}%` } },
          { email: { _ilike: `%${trimmedSearch}%` } },
          { phoneNumber: { _ilike: `%${trimmedSearch}%` } },
        ],
      });
    }

    if (roleFilter !== "all") {
      conditions.push({
        roles: { role: { _eq: roleFilter as AuthRoles_Enum } },
      });
    }

    if (orgStatusFilter === "active") {
      conditions.push({
        user_organizations: {
          organization: { status: { _eq: Organization_Status_Enum.Active } },
        },
      });
    } else if (orgStatusFilter === "inactive") {
      conditions.push({
        _and: [
          {
            user_organizations_aggregate: {
              count: { predicate: { _gt: 0 } },
            },
          },
          {
            _not: {
              user_organizations: {
                organization: {
                  status: { _eq: Organization_Status_Enum.Active },
                },
              },
            },
          },
        ],
      });
    } else if (orgStatusFilter === "no_org") {
      conditions.push({
        user_organizations_aggregate: {
          count: { predicate: { _eq: 0 } },
        },
      });
    }

    if (conditions.length === 0) return {};
    if (conditions.length === 1) return conditions[0];
    return { _and: conditions };
  }, [debouncedSearchTerm, roleFilter, orgStatusFilter]);

  const queryVariables = useMemo<GetUsersDataQueryVariables>(
    () => ({
      limit: PAGE_SIZE,
      offset: (currentPage - 1) * PAGE_SIZE,
      order_by: [{ createdAt: Order_By.Desc }],
      where: whereClause,
    }),
    [currentPage, whereClause]
  );

  const { data, loading, error, refetch } = useGetUsersDataQuery({
    variables: queryVariables,
    errorPolicy: "all",
  });

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const pageParam = urlParams.get("page");
    const searchParam = urlParams.get("search");

    if (pageParam && !Number.isNaN(Number.parseInt(pageParam, 10))) {
      setCurrentPage(Number.parseInt(pageParam, 10));
    }
    if (searchParam) {
      setSearchTerm(searchParam);
      setDebouncedSearchTerm(searchParam);
    }
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 400);

    return () => window.clearTimeout(timer);
  }, [searchTerm]);

  const updateURL = (page: number, search: string) => {
    const params = new URLSearchParams();
    if (page > 1) params.set("page", page.toString());
    if (search) params.set("search", search);

    const newUrl =
      window.location.pathname +
      (params.toString() ? `?${params.toString()}` : "");
    window.history.replaceState({}, "", newUrl);
  };

  useEffect(() => {
    if (debouncedSearchTerm !== searchTerm) return;
    setCurrentPage(1);
    updateURL(1, debouncedSearchTerm);
  }, [debouncedSearchTerm, searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [roleFilter, orgStatusFilter]);

  const users = data?.users ?? [];
  const totalCount = data?.usersAggregate?.aggregate?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const hasNextPage = currentPage < totalPages;
  const hasPrevPage = currentPage > 1;

  const roleOptions = ["INVESTOR", "AGENCY", "LISTER", "SEEKER"];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    updateURL(page, debouncedSearchTerm);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const showingFrom = totalCount === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const showingTo = Math.min(currentPage * PAGE_SIZE, totalCount);

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-center">
              <svg
                className="w-5 h-5 text-red-400 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <h3 className="text-red-800 font-medium">Error Loading Users</h3>
            </div>
            <p className="text-red-700 mt-2">
              {error.message ||
                "An unexpected error occurred while loading users."}
            </p>
            <button
              onClick={() => refetch()}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500">
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Users</h1>
          <p className="text-gray-600">
            Explore platform users, contact details, roles, and organization
            subscription status.
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border mb-6 p-6">
          <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Search by user, email, phone, role, org..."
                  className="h-10 w-80 max-w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {searchTerm !== debouncedSearchTerm && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-300 border-t-blue-600" />
                  </div>
                )}
              </div>

              <select
                value={roleFilter}
                onChange={(event) => setRoleFilter(event.target.value)}
                className="h-10 rounded-lg border-2 border-gray-300 bg-white px-3 text-sm text-gray-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-500">
                <option value="all">All roles</option>
                {roleOptions.map((role) => (
                  <option key={role} value={role}>
                    {role.replaceAll("_", " ")}
                  </option>
                ))}
              </select>

              <select
                value={orgStatusFilter}
                onChange={(event) =>
                  setOrgStatusFilter(event.target.value as OrgStatusFilter)
                }
                className="h-10 rounded-lg border-2 border-gray-300 bg-white px-3 text-sm text-gray-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-500">
                <option value="all">All organizations</option>
                <option value="active">Active orgs</option>
                <option value="inactive">Inactive orgs</option>
                <option value="no_org">No organization</option>
              </select>

              <div className="text-sm text-gray-600">
                {loading
                  ? "Loading..."
                  : `${users.length} shown on this page`}
              </div>
            </div>

            <div className="text-sm text-gray-600">
              {loading
                ? "Loading..."
                : `Showing ${showingFrom}-${showingTo} of ${totalCount} users`}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1000px] table-fixed">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-900 w-[180px] min-w-[140px]">
                    User
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-900 w-[200px] max-w-[200px]">
                    Contact
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-900 w-[160px] min-w-[140px]">
                    Created
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-900 w-[140px] min-w-[100px]">
                    Roles
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-900 min-w-[180px]">
                    Organization
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-900 min-w-[160px]">
                    Subscription
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-900 w-[100px] min-w-[80px]">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-900 w-[120px] min-w-[110px]">
                    WhatsApp
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  Array.from({ length: PAGE_SIZE }, (_, index) => (
                    <tr key={`loading-${index}`} className="animate-pulse">
                      <td className="px-4 py-3">
                        <div className="h-4 bg-gray-200 rounded w-full max-w-[140px]" />
                      </td>
                      <td className="px-4 py-3">
                        <div className="h-4 bg-gray-200 rounded w-full max-w-[180px]" />
                      </td>
                      <td className="px-4 py-3">
                        <div className="h-4 bg-gray-200 rounded w-full max-w-[140px]" />
                      </td>
                      <td className="px-4 py-3">
                        <div className="h-6 bg-gray-200 rounded w-full max-w-[100px]" />
                      </td>
                      <td className="px-4 py-3">
                        <div className="h-10 bg-gray-200 rounded w-full max-w-[160px]" />
                      </td>
                      <td className="px-4 py-3">
                        <div className="h-10 bg-gray-200 rounded w-full max-w-[140px]" />
                      </td>
                      <td className="px-4 py-3">
                        <div className="h-6 bg-gray-200 rounded w-full max-w-[80px]" />
                      </td>
                      <td className="px-4 py-3">
                        <div className="h-6 bg-gray-200 rounded w-full max-w-[90px]" />
                      </td>
                    </tr>
                  ))
                ) : users.length > 0 ? (
                  users.map((user) => {
                    const roles = getUserRoles(user);
                    const organizations = user.user_organizations;
                    const primaryOrg = organizations[0]?.organization;
                    const latestSubscription =
                      primaryOrg?.subscriptions?.[0] ?? null;

                    return (
                      <tr
                        key={`${user.id}`}
                        className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3">
                          <div className="font-medium text-sm text-gray-900">
                            {user.displayName || "Unknown user"}
                          </div>
                        </td>

                        <td className="px-4 py-3 max-w-[200px]">
                          <div className="text-sm text-gray-800 truncate" title={user.email ?? undefined}>
                            {user.email ? `${user.email}` : "-"}
                          </div>
                          <div className="text-xs text-gray-500 mt-1 truncate" title={user.phoneNumber ?? undefined}>
                            {user.phoneNumber || "No phone"}
                          </div>
                        </td>

                        <td className="px-4 py-3">
                          <div className="text-sm text-gray-800">
                            {formatDateTime(user.createdAt)}
                          </div>
                        </td>

                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-2">
                            {roles.length > 0 ? (
                              roles.map((role) => (
                                <span
                                  key={`${user.id}-${role}`}
                                  className="inline-flex items-center rounded-full bg-blue-100 text-blue-800 px-2.5 py-0.5 text-xs font-medium">
                                  {role.replaceAll("_", " ")}
                                </span>
                              ))
                            ) : (
                              <span className="text-xs text-gray-500">
                                No role
                              </span>
                            )}
                          </div>
                        </td>

                        <td className="px-4 py-3">
                          {primaryOrg ? (
                            <div className="space-y-1">
                              <div className="text-sm font-medium text-gray-900">
                                {primaryOrg.display_name}
                              </div>
                              <div className="text-xs text-gray-500">
                                {primaryOrg.parent?.display_name
                                  ? `Parent: ${primaryOrg.parent.display_name}`
                                  : "No parent organization"}
                              </div>
                              {organizations.length > 1 && (
                                <div className="text-xs text-gray-500">
                                  +{organizations.length - 1} more organizations
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-500">
                              ----------
                            </span>
                          )}
                        </td>

                        <td className="px-4 py-3">
                          {latestSubscription ? (
                            <div className="space-y-1">
                              <div className="text-sm text-gray-800">
                                {formatDate(
                                  `${latestSubscription.start_date ?? ""}`
                                )}{" "}
                                -{" "}
                                {formatDate(
                                  `${latestSubscription.end_date ?? ""}`
                                )}
                              </div>
                              <span
                                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getSubscriptionPillClasses(
                                  latestSubscription.status
                                )}`}>
                                {(latestSubscription.status || "unknown")
                                  .toString()
                                  .replaceAll("_", " ")}
                              </span>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-500">
                              None
                            </span>
                          )}
                        </td>

                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getOrgStatusPillClasses(
                              primaryOrg?.status
                            )}`}>
                            {primaryOrg?.status
                              ? primaryOrg.status.replaceAll("_", " ")
                              : "No Org"}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {user.phoneNumber && "phoneNumberVerified" in user && user.phoneNumberVerified === true ? (
                            <Link
                              className="text-sm font-medium text-blue-600 hover:text-blue-800"
                              to={`/whatsapp-leads?userId=${user.id}`}
                            >
                              Configure
                            </Link>
                          ) : (
                            <span className="text-xs text-gray-400">Phone unverified</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center">
                      <div className="text-gray-500">
                        No users found
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={!hasPrevPage}
                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md ${
                  hasPrevPage
                    ? "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                    : "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed"
                }`}>
                Previous
              </button>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={!hasNextPage}
                className={`ml-3 relative inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md ${
                  hasNextPage
                    ? "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                    : "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed"
                }`}>
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{showingFrom}</span> to{" "}
                  <span className="font-medium">{showingTo}</span> of{" "}
                  <span className="font-medium">{totalCount}</span> results
                </p>
              </div>
              <div>
                <nav
                  className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                  aria-label="Pagination">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={!hasPrevPage}
                    className={`relative inline-flex items-center px-2 py-2 rounded-l-md border text-sm font-medium ${
                      hasPrevPage
                        ? "border-gray-300 bg-white text-gray-500 hover:bg-gray-50"
                        : "border-gray-200 bg-gray-100 text-gray-300 cursor-not-allowed"
                    }`}>
                    <span className="sr-only">Previous</span>
                    <svg
                      className="h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true">
                      <path
                        fillRule="evenodd"
                        d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>

                  {getPageNumbers(totalPages, currentPage).map(
                    (pageNum, index) => {
                      if (pageNum === "...") {
                        return (
                          <span
                            key={`ellipsis-${index}`}
                            className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                            ...
                          </span>
                        );
                      }

                      const isCurrentPage = pageNum === currentPage;
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum as number)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            isCurrentPage
                              ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                              : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                          }`}>
                          {pageNum}
                        </button>
                      );
                    }
                  )}

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={!hasNextPage}
                    className={`relative inline-flex items-center px-2 py-2 rounded-r-md border text-sm font-medium ${
                      hasNextPage
                        ? "border-gray-300 bg-white text-gray-500 hover:bg-gray-50"
                        : "border-gray-200 bg-gray-100 text-gray-300 cursor-not-allowed"
                    }`}>
                    <span className="sr-only">Next</span>
                    <svg
                      className="h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true">
                      <path
                        fillRule="evenodd"
                        d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
